import React, { useState } from 'react';
import { prop } from 'ramda';

import { InputsRow } from '@interlayer/js/app/components/common/Fields/Input/components/InputsRow';
import {
    AddressManuallyFields
} from '@interlayer/js/app/components/main/Registration/components/fields/AddressFields/components/AddressManuallyFields';
import {
    AddressPostCodeFields
} from '@interlayer/js/app/components/main/Registration/components/fields/AddressFields/components/AddressPostCodeFields';

import { Button } from 'js/components/common/Button';
import ErrorMessage from 'js/components/common/Fields/ErrorMessage';
import Form from 'js/components/common/Form';

import { filterFormDataOnSend } from '@interlayer/js/app/components/main/Registration/hooks/useRegistrationFetch';
import { changeDetails } from '@interlayer/js/app/components/main/Registration/server';
import { VALIDATION_FIELDS_CONTACT_INFORMATION } from '@interlayer/js/app/components/main/UserDetails/components/ContactInformation/constants';
import { useValidationRules } from '@interlayer/js/app/hooks/useValidationRules';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { useFetchWithTwoFa } from '@interlayer/js/components/popups/TwoFactorAuthenticationPopup/buzz/hooks/useFetchWithTwoFa';

import { useTranslations } from 'js/hooks/i18n';
import { useFetchUserDetails } from 'js/hooks/user';
import { useUserSelector } from 'js/store/hooks';

import style from './ContactInfoUpdateForm.scss';

export const ContactInfoUpdateForm = ({ closePopup, closeForm }) => {
    const userDetails = useUserSelector(prop('userDetails'));
    const fetchWithTwoFa = useFetchWithTwoFa();
    const fetchUserDetails = useFetchUserDetails();
    const validationConfig = useValidationRules(VALIDATION_FIELDS_CONTACT_INFORMATION, 'ContactInfoUpdateForm');
    const [isShowManuallyAddress, setIsShowManuallyAddress] = useState(false);
    const t = useTranslations('playtech.mobile.system.userdetails.');
    const tEnterDraw = useTranslations('playtech.mobile.system.enter-draw.contact-popup.');

    const inputsUserInfo = [
        {
            name: 'email',
            placeholder: t('emailAddress')
        },
        {
            name: 'phone',
            placeholder: t('mobileNumber'),
            isOnlyNumber: true,
            inputMode: 'numeric',
            pattern: '\\d*'
        }
    ];

    const initialValues = {
        house: userDetails.addressDetails?.buildingNumber || userDetails.address?.split(' ')[0],
        street: userDetails.addressDetails?.streetName || userDetails.address
            ?.split(' ')
            .slice(1)
            .join(' '),
        city: userDetails.city || '',
        state: userDetails.state || '',
        zip: userDetails.zip,
        phone: userDetails.cellphone,
        email: userDetails.email
    };

    const handlerSubmit = (values) => fetchWithTwoFa({
        fetch: () => changeDetails(filterFormDataOnSend(values)),
        afterFetch: () => fetchUserDetails().then(closePopup)
    });

    return (
        <Form
            onSubmit={handlerSubmit}
            validationConfig={validationConfig}
            initialValues={initialValues}
            render={({ submitting, submitError, handleSubmit, pristine }) => (
                <form
                    onSubmit={handleSubmit}
                    className={style.form}
                    data-testid="ContactInfoUpdateForm__form"
                >
                    {submitError && (
                        <ErrorMessage
                            className={style.error}
                            error={submitError}
                            animate
                            isFormError
                        />
                    )}

                    <AddressPostCodeFields
                        isNearestClub={false}
                        handlerSwitchManually={setIsShowManuallyAddress}
                        className={clsx({ hidden: isShowManuallyAddress })}
                    />

                    <AddressManuallyFields
                        isShowLinkAuto
                        handlerSwitchManually={setIsShowManuallyAddress}
                        className={clsx({ hidden: !isShowManuallyAddress })}
                    />

                    <InputsRow options={inputsUserInfo} />

                    <div className={style.buttons}>
                        <Button
                            size="small"
                            className={style.button}
                            onClick={closeForm}
                            data-testid="ContactInfoUpdateForm__closeFrom"
                        >
                            {tEnterDraw('button.back')}
                        </Button>

                        <Button
                            type="submit"
                            size="small"
                            view="secondary"
                            disabled={submitting || pristine}
                            className={style.button}
                            data-testid="ContactInfoUpdateForm__submit"
                        >
                            {tEnterDraw('button.update')}
                        </Button>
                    </div>
                </form>
            )}
        />
    );
};
